import { useCallback, useMemo } from 'react'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CaptionLG, CaptionMD, EyebrowMD, TitleMD } from '@/atoms/Text'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { formatPrice } from '@/services/PriceFormatter'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { formatDate } from '@/views/Buyout/BuyoutView'

interface Props {
  guildMembershipIncluded: boolean
}

const ExpressLineItems = ({ guildMembershipIncluded }: Props) => {
  const { t } = useTranslate('pif')
  const { selectedOffer } = usePayItForwardOfferData()

  const priceFormatter = useCallback(
    (ignoreEmptyDecimal = true, overrideAmount?: string) => {
      const amount = overrideAmount ? overrideAmount : selectedOffer?.price || '0'

      return formatPrice(Number(amount), {
        currency: selectedOffer?.currency || 'USD',
        ignoreEmptyDecimal,
      })
    },
    [selectedOffer],
  )

  const numberOfTickets = useMemo(() => {
    if (!selectedOffer) return 0
    return Math.floor(selectedOffer.price / 15 / 100)
  }, [selectedOffer])

  const nextMonthDate = new Date(new Date().setMonth(new Date().getMonth() + 1))

  return (
    <FlexColumn>
      <FlexRow className="justify-between gap-1">
        <TitleMD>{t('expressCheckout', 'Express Checkout')}</TitleMD>
        <CaptionMD color="core-gray-950" weight="semibold" className="uppercase">
          <Translate
            t={t}
            i18nKey="priceToday2"
            components={{ priceFormat: <span className="text-[1.25em]" /> }}
            values={{ price: priceFormatter(true) }}
            defaults="<priceFormat>{{price}}</priceFormat> today"
          />
        </CaptionMD>
      </FlexRow>

      {guildMembershipIncluded && (
        <FlexColumn className="mt-4">
          <FlexRow className="mb-2 items-center justify-between gap-1">
            <CaptionLG>{t('payItForwardNTickets', 'Pay it Forward {{n}} Tickets', { n: numberOfTickets })}</CaptionLG>
            <CaptionLG>{priceFormatter(false)}</CaptionLG>
          </FlexRow>

          <FlexRow className="mb-1 items-center justify-between gap-1">
            <CaptionLG>{t('angelGuildPremiumMembership', 'Angel Guild Premium Membership')}</CaptionLG>
            <CaptionLG>{priceFormatter(false, '0')}</CaptionLG>
          </FlexRow>
          <EyebrowMD color="core-gray-600" weight="semibold" className="mb-1">
            {t('oneFreeMonth', '1 free month')}
          </EyebrowMD>

          <CaptionMD color="core-gray-600">
            <Translate
              t={t}
              i18nKey="20BilledOnDate'"
              defaults="{{price}} billed on <bold>{{date}}</bold>"
              components={{ bold: <b className="text-core-gray-900" /> }}
              values={{
                price: priceFormatter(false, '2000'),
                date: formatDate(nextMonthDate.toDateString()),
              }}
            />
          </CaptionMD>
        </FlexColumn>
      )}
    </FlexColumn>
  )
}

export default ExpressLineItems
